"use client";

import { useState, useEffect, useCallback } from "react";
import Icon from "@/components/ui/AppIcon";

interface Question {
  id: string;
  module: string;
  question: string;
  options: string[];
  microFeedback?: string;
}

interface DiagnosticResponse {
  questionId: string;
  answer: string;
}

const STORAGE_KEY = "leadership_diagnostic_progress";

export default function DiagnosticFlow() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState<DiagnosticResponse[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [progressRestored, setProgressRestored] = useState(false);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [savedProgress, setSavedProgress] = useState<{ index: number; responses: DiagnosticResponse[] } | null>(null);

  const questions: Question[] = [
    {
      id: "q1",
      module: "Self-Leadership Under Pressure",
      question: "When pressure increases, my default response is:",
      options: [
        "Narrow focus and push harder",
        "Pause and analyze longer",
        "Take direct control",
        "Function outwardly while feeling internal friction",
      ],
      microFeedback: "Pressure reveals operating systems, not character.",
    },
    {
      id: "q2",
      module: "Self-Leadership Under Pressure",
      question:
        "I notice inconsistency between my expectations of others and my own behavior:",
      options: ["Often", "Sometimes", "Rarely", "Almost never"],
    },
    {
      id: "q3",
      module: "Self-Leadership Under Pressure",
      question: "Under uncertainty, I rely most on:",
      options: [
        "Data and structure",
        "Pattern recognition",
        "Past experience",
        "External validation",
      ],
    },
    {
      id: "q4",
      module: "Decision-Making",
      question: "I delay decisions primarily because:",
      options: [
        "Information feels incomplete",
        "Consequences feel complex",
        "I want to avoid irreversible outcomes",
        "I usually decide quickly",
      ],
    },
    {
      id: "q5",
      module: "Decision-Making",
      question: "I step in personally when:",
      options: [
        "Standards drop",
        "Speed matters",
        "Control feels unstable",
        "I generally don't",
      ],
    },
    {
      id: "q6",
      module: "Decision-Making",
      question: "Delegating ownership feels:",
      options: [
        "Necessary but uncomfortable",
        "Natural",
        "Risky",
        "Context-dependent",
      ],
    },
    {
      id: "q7",
      module: "Leadership Identity",
      question: "My leadership role feels:",
      options: [
        "Clear and embodied",
        "Clear but draining",
        "Functional but misaligned",
        "Unclear",
      ],
    },
    {
      id: "q8",
      module: "Leadership Identity",
      question: "I feel responsible for:",
      options: [
        "Outcomes",
        "People's emotional states",
        "Systems and structure",
        "All of the above",
      ],
    },
    {
      id: "q9",
      module: "Leadership Identity",
      question:
        "The gap between how I'm perceived and how I feel internally is:",
      options: ["Large", "Moderate", "Small", "Nonexistent"],
    },
    {
      id: "q10",
      module: "Systemic Blind Spots",
      question: "Problems I face tend to:",
      options: [
        "Repeat in different forms",
        "Be situational",
        "Be externally driven",
        "Rarely repeat",
      ],
    },
    {
      id: "q11",
      module: "Systemic Blind Spots",
      question: "Feedback I receive is usually:",
      options: ["Filtered", "Direct", "Incomplete", "Avoided"],
    },
    {
      id: "q12",
      module: "Systemic Blind Spots",
      question: "When something breaks, my first instinct is to ask:",
      options: [
        "What failed in the system",
        "Who is responsible",
        "What I missed",
        "How fast it can be fixed",
      ],
    },
    {
      id: "q13",
      module: "Integration",
      question: "My current operating capacity is:",
      options: [
        "Above sustainable levels",
        "At capacity",
        "Below capacity",
        "In recovery",
      ],
    },
    {
      id: "q14",
      module: "Integration",
      question: "What I need most right now is:",
      options: [
        "Clarity",
        "Alignment",
        "Structure",
        "Reduced internal noise",
      ],
    },
    {
      id: "q15",
      module: "Integration",
      question:
        "I am willing to see uncomfortable patterns if they are useful:",
      options: ["Yes", "Mostly", "Unsure", "No"],
    },
  ];

  // Load saved progress on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.index > 0 && parsed.responses?.length > 0) {
          setSavedProgress(parsed);
          setShowRestorePrompt(true);
        }
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Save progress on every response change
  const saveProgress = useCallback((index: number, resps: DiagnosticResponse[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ index, responses: resps }));
    } catch {
      // Ignore storage errors
    }
  }, []);

  const clearProgress = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }
  }, []);

  const handleRestoreProgress = () => {
    if (savedProgress) {
      setCurrentIndex(savedProgress.index);
      setResponses(savedProgress.responses);
      setProgressRestored(true);
    }
    setShowRestorePrompt(false);
  };

  const handleStartFresh = () => {
    clearProgress();
    setShowRestorePrompt(false);
  };

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  const handleAnswer = (answer: string) => {
    const newResponse: DiagnosticResponse = {
      questionId: currentQuestion.id,
      answer,
    };

    const newResponses = [...responses, newResponse];
    setResponses(newResponses);
    saveProgress(currentIndex + 1, newResponses);

    if (currentQuestion.microFeedback) {
      setShowFeedback(true);
      setTimeout(() => {
        setShowFeedback(false);
        proceedToNext(newResponses);
      }, 2500);
    } else {
      proceedToNext(newResponses);
    }
  };

  const proceedToNext = (currentResponses: DiagnosticResponse[]) => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      handleSubmit(currentResponses);
    }
  };

  const handleSubmit = async (finalResponses: DiagnosticResponse[]) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Simulate API submission delay
      await new Promise((resolve) => setTimeout(resolve, 1500));
      clearProgress();
      setIsSubmitted(true);
    } catch {
      setSubmitError(
        "There was an issue submitting your diagnostic. Please try again."
      );
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const newResponses = responses.slice(0, -1);
      setCurrentIndex(newIndex);
      setResponses(newResponses);
      saveProgress(newIndex, newResponses);
    }
  };

  // Restore prompt
  if (showRestorePrompt) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-lg mx-auto text-center space-y-8 animate-fadeIn">
          <div className="size-16 rounded-full bg-accent/10 flex items-center justify-center text-accent mx-auto">
            <Icon name="BookmarkIcon" size={28} />
          </div>
          <div className="space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Resume where you left off?
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              You have saved progress from a previous session — question{" "}
              {(savedProgress?.index ?? 0) + 1} of {questions.length}.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleRestoreProgress}
              className="px-8 py-3 bg-accent text-accent-foreground rounded-full text-sm font-bold uppercase tracking-widest hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Continue
            </button>
            <button
              onClick={handleStartFresh}
              className="px-8 py-3 bg-card border border-border text-foreground rounded-full text-sm font-bold uppercase tracking-widest hover:bg-secondary transition-all focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Start Fresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Submitting state
  if (isSubmitting) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-lg mx-auto text-center space-y-8 animate-fadeIn">
          <div className="size-16 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto" />
          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-foreground">
              Submitting your diagnostic...
            </h2>
            <p className="text-base text-muted-foreground">
              Your responses are being securely transmitted.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (submitError) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-lg mx-auto text-center space-y-8 animate-fadeIn">
          <div className="size-16 rounded-full bg-destructive/10 flex items-center justify-center text-destructive mx-auto">
            <Icon name="ExclamationCircleIcon" size={28} />
          </div>
          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-foreground">
              Submission failed
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              {submitError}
            </p>
          </div>
          <button
            onClick={() => handleSubmit(responses)}
            className="px-8 py-3 bg-accent text-accent-foreground rounded-full text-sm font-bold uppercase tracking-widest hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-2xl mx-auto text-center space-y-8 animate-fadeIn">
          <div className="size-20 rounded-full bg-success/10 flex items-center justify-center text-success mx-auto">
            <Icon name="CheckCircleIcon" size={32} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
            Thank you for completing the Leadership Diagnostic.
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Your responses have been securely received.
          </p>
          <div className="glass-card p-8 text-left space-y-4">
            <p className="text-base text-foreground leading-relaxed">
              Horváth Anett will personally review your diagnostic patterns and
              reach out to you shortly.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              No charts. No scores. No automated interpretation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showFeedback && currentQuestion.microFeedback) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-2xl mx-auto text-center animate-fadeIn">
          <div className="glass-card p-12 border-l-4 border-accent">
            <p className="text-2xl md:text-3xl text-foreground leading-relaxed italic">
              {currentQuestion.microFeedback}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex flex-col">
      {/* Progress Bar */}
      <div
        className="w-full h-1 bg-border"
        role="progressbar"
        aria-valuenow={currentIndex + 1}
        aria-valuemin={1}
        aria-valuemax={questions.length}
        aria-label={`Question ${currentIndex + 1} of ${questions.length}`}
      >
        <div
          className="h-full bg-accent transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Progress Restored Banner */}
      {progressRestored && (
        <div className="bg-accent/10 border-b border-accent/20 px-6 py-2 text-center">
          <p className="text-xs text-accent font-medium">
            Progress restored — continuing from question {currentIndex + 1}
          </p>
        </div>
      )}

      {/* Question Container */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12">
        <div className="max-w-3xl mx-auto w-full space-y-8 md:space-y-12 animate-fadeIn">
          {/* Module Label */}
          <div className="text-center">
            <span className="inline-flex items-center px-4 py-2 rounded-full border border-border bg-secondary/50 text-xs font-bold uppercase tracking-widest text-muted-foreground">
              {currentQuestion.module}
            </span>
          </div>

          {/* Question */}
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight text-center">
            {currentQuestion.question}
          </h2>

          {/* Reassurance */}
          <p className="text-center text-sm text-muted-foreground italic">
            There are no wrong answers.
          </p>

          {/* Options */}
          <div className="space-y-3 md:space-y-4" role="group" aria-label="Answer options">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                className="w-full glass-card p-4 md:p-6 text-left hover:border-accent/50 hover:bg-accent/5 transition-all focus:outline-none focus:ring-2 focus:ring-ring group"
                aria-label={`Answer: ${option}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-base md:text-lg text-foreground group-hover:text-accent transition-colors">
                    {option}
                  </span>
                  <Icon
                    name="ChevronRightIcon"
                    size={20}
                    className="text-muted flex-shrink-0 group-hover:text-accent transition-colors"
                  />
                </div>
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-4 md:pt-8">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`flex items-center gap-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring px-4 py-2 rounded-full ${
                currentIndex === 0
                  ? "text-muted cursor-not-allowed opacity-40" :"text-foreground hover:text-accent"
              }`}
              aria-label="Go to previous question"
            >
              <Icon name="ChevronLeftIcon" size={20} />
              <span className="hidden sm:inline">Previous</span>
            </button>
            <span className="text-sm text-muted-foreground font-medium">
              {currentIndex + 1} / {questions.length}
            </span>
            <div className="w-16 sm:w-24" />
          </div>
        </div>
      </div>
    </div>
  );
}